import type { FontSizeMode } from '../types';

const FONT_SIZE_MODE_KEY = 'mealPai_font_size_mode';
const FONT_SIZE_KEY = 'mealPai_font_size';
const DEFAULT_MULTIPLIER = 100;

/** Layout uses a fixed root size so buttons/spacing stay stable. */
export const LAYOUT_ROOT_PX = 16;
export const MIN_TEXT_SCALE = 0.875;
export const MAX_TEXT_SCALE = 1.25;
export const MAX_FONT_MULTIPLIER = Math.round(MAX_TEXT_SCALE * 100);

function clampScale(value: number): number {
    return Math.min(MAX_TEXT_SCALE, Math.max(MIN_TEXT_SCALE, value));
}

export function getFontSizeMode(): FontSizeMode {
    const stored = localStorage.getItem(FONT_SIZE_MODE_KEY);
    if (stored === 'system' || stored === 'custom') {
        return stored;
    }
    const legacy = localStorage.getItem(FONT_SIZE_KEY);
    if (legacy && parseInt(legacy, 10) !== DEFAULT_MULTIPLIER) {
        return 'custom';
    }
    return 'system';
}

export function getFontSizeMultiplier(): number {
    const stored = localStorage.getItem(FONT_SIZE_KEY);
    if (stored) {
        const parsed = parseInt(stored, 10);
        if (!Number.isNaN(parsed)) {
            return Math.min(MAX_FONT_MULTIPLIER, Math.max(Math.round(MIN_TEXT_SCALE * 100), parsed));
        }
    }
    return DEFAULT_MULTIPLIER;
}

/** Read OS/browser text preference before we lock layout sizing. */
function readSystemTextScale(): number {
    if (typeof window === 'undefined') return 1;
    const root = document.documentElement;
    root.style.removeProperty('font-size');
    const computedPx = parseFloat(window.getComputedStyle(root).fontSize);
    if (!Number.isFinite(computedPx) || computedPx <= 0) return 1;
    return clampScale(computedPx / LAYOUT_ROOT_PX);
}

export function applyFontSize(mode: FontSizeMode, multiplier: number = DEFAULT_MULTIPLIER): void {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    const textScale =
        mode === 'custom'
            ? clampScale(multiplier / 100)
            : readSystemTextScale();

    root.style.fontSize = `${LAYOUT_ROOT_PX}px`;
    root.style.setProperty('-webkit-text-size-adjust', '100%');
    root.style.setProperty('text-size-adjust', '100%');
    root.style.setProperty('--mealpai-text-scale', String(textScale));
}

export function saveFontSizePreferences(mode: FontSizeMode, multiplier: number): void {
    const clampedMultiplier = Math.min(MAX_FONT_MULTIPLIER, Math.max(Math.round(MIN_TEXT_SCALE * 100), multiplier));
    localStorage.setItem(FONT_SIZE_MODE_KEY, mode);
    localStorage.setItem(FONT_SIZE_KEY, clampedMultiplier.toString());
    applyFontSize(mode, clampedMultiplier);
}
